/**
 * Compliance Roster Page
 *
 * Editable single source of truth that maps each compliance role
 * (Security Officer, Privacy Officer, IT Asset Manager, etc.) to a
 * named individual + email. Used by Policy PDFs to auto-render a
 * "Personnel Assignments" section.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Save, UserCheck, Mail, Plus, Trash2, Search } from 'lucide-react';
import { Card } from '../components/ui-custom/ComplianceUI';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

export default function RosterPage() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [newRole, setNewRole] = useState({ title: '', description: '' });

  const fetchRoster = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/roster`, { credentials: 'include' });
      if (!res.ok) {
        toast.error('Failed to load roster');
        return;
      }
      const data = await res.json();
      setRoles(data.roles || []);
      // Seed local drafts from server values
      const next = {};
      (data.roles || []).forEach((r) => {
        next[r.role_key] = {
          person_name: r.person_name || '',
          person_email: r.person_email || '',
          backup_name: r.backup_name || '',
          backup_email: r.backup_email || '',
        };
      });
      setDrafts(next);
    } catch (e) {
      console.error(e);
      toast.error('Roster load error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoster();
  }, [fetchRoster]);

  const isDirty = (role) => {
    const d = drafts[role.role_key] || {};
    return (
      d.person_name !== (role.person_name || '') ||
      d.person_email !== (role.person_email || '') ||
      d.backup_name !== (role.backup_name || '') ||
      d.backup_email !== (role.backup_email || '')
    );
  };

  const updateField = (roleKey, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [roleKey]: { ...(prev[roleKey] || {}), [field]: value },
    }));
  };

  const saveRole = async (role) => {
    const draft = drafts[role.role_key];
    if (!draft) return;

    // Light email validation
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (draft.person_email && !emailRe.test(draft.person_email)) {
      toast.error('Invalid primary email');
      return;
    }
    if (draft.backup_email && !emailRe.test(draft.backup_email)) {
      toast.error('Invalid backup email');
      return;
    }

    setSavingKey(role.role_key);
    try {
      const res = await fetch(`${API_URL}/api/roster/${role.role_key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          person_name: draft.person_name || null,
          person_email: draft.person_email || null,
          backup_name: draft.backup_name || null,
          backup_email: draft.backup_email || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Save failed');
        return;
      }
      toast.success(`${role.title} updated`);
      await fetchRoster();
    } catch (e) {
      console.error(e);
      toast.error('Save error');
    } finally {
      setSavingKey(null);
    }
  };

  const handleAddRole = async () => {
    if (!newRole.title.trim()) {
      toast.error('Role title is required');
      return;
    }
    const role_key = newRole.title.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    try {
      const res = await fetch(`${API_URL}/api/roster`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          role_key,
          title: newRole.title.trim(),
          description: newRole.description.trim() || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not create role');
        return;
      }
      toast.success(`Added "${newRole.title}"`);
      setNewRole({ title: '', description: '' });
      setShowAdd(false);
      await fetchRoster();
    } catch (e) {
      console.error(e);
      toast.error('Create error');
    }
  };

  const handleDeleteRole = async (role) => {
    if (!window.confirm(`Remove role "${role.title}"? This will also drop any assigned person.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/roster/${role.role_key}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        toast.error('Delete failed');
        return;
      }
      toast.success(`Removed "${role.title}"`);
      await fetchRoster();
    } catch (e) {
      console.error(e);
      toast.error('Delete error');
    }
  };

  const filtered = roles.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      r.title?.toLowerCase().includes(q) ||
      r.person_name?.toLowerCase().includes(q) ||
      r.person_email?.toLowerCase().includes(q)
    );
  });

  const assignedCount = roles.filter((r) => r.person_name && r.person_email).length;

  return (
    <div className="roster-page" data-testid="roster-page">
      <div className="page-header">
        <div>
          <h1>Compliance Roster</h1>
          <p>
            Single source of truth for who owns each compliance role.
            Assignments shown here appear automatically in policy PDFs as a
            "Personnel Assignments" section — no policy revisions needed when
            staff change.
          </p>
        </div>
        <div className="roster-stats">
          <div className="roster-stat">
            <span className="stat-value">{assignedCount}</span>
            <span className="stat-label">Assigned</span>
          </div>
          <div className="roster-stat">
            <span className="stat-value">{roles.length - assignedCount}</span>
            <span className="stat-label">Unassigned</span>
          </div>
        </div>
      </div>

      <div className="roster-toolbar">
        <div className="roster-search">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search by role, name, or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="roster-search"
          />
        </div>
        <button
          className="btn-primary"
          onClick={() => setShowAdd(!showAdd)}
          data-testid="roster-add-toggle"
        >
          <Plus size={16} /> {showAdd ? 'Cancel' : 'Add Custom Role'}
        </button>
      </div>

      {showAdd && (
        <Card className="roster-add-card" data-testid="roster-add-card">
          <h3>Add a new role</h3>
          <div className="roster-form-row">
            <label>
              Role title
              <input
                type="text"
                placeholder="e.g. Cloud Security Architect"
                value={newRole.title}
                onChange={(e) => setNewRole({ ...newRole, title: e.target.value })}
                data-testid="roster-new-title"
              />
            </label>
            <label>
              Short description (optional)
              <input
                type="text"
                placeholder="What this role owns"
                value={newRole.description}
                onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
                data-testid="roster-new-description"
              />
            </label>
          </div>
          <button className="btn-primary" onClick={handleAddRole} data-testid="roster-create-role">
            Create role
          </button>
        </Card>
      )}

      {loading ? (
        <div className="roster-loading">Loading roster…</div>
      ) : (
        <div className="roster-grid">
          {filtered.length === 0 ? (
            <div className="empty-state">No roles match your search.</div>
          ) : (
            filtered.map((role) => {
              const draft = drafts[role.role_key] || {};
              const dirty = isDirty(role);
              const assigned = role.person_name && role.person_email;
              return (
                <Card
                  key={role.role_key}
                  className={`roster-card ${assigned ? 'assigned' : 'unassigned'}`}
                  data-testid={`roster-card-${role.role_key}`}
                >
                  <div className="roster-card-header">
                    <div className="roster-role-meta">
                      <UserCheck size={18} className="roster-icon" />
                      <div>
                        <h3>{role.title}</h3>
                        {role.description && <p className="roster-desc">{role.description}</p>}
                      </div>
                    </div>
                    <button
                      className="roster-delete"
                      onClick={() => handleDeleteRole(role)}
                      title="Remove this role"
                      data-testid={`roster-delete-${role.role_key}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>

                  <div className="roster-fields">
                    <label>
                      Primary owner
                      <input
                        type="text"
                        placeholder="Full name"
                        value={draft.person_name || ''}
                        onChange={(e) => updateField(role.role_key, 'person_name', e.target.value)}
                        data-testid={`roster-name-${role.role_key}`}
                      />
                    </label>
                    <label>
                      <Mail size={12} /> Primary email
                      <input
                        type="email"
                        placeholder="name@threebie.com"
                        value={draft.person_email || ''}
                        onChange={(e) => updateField(role.role_key, 'person_email', e.target.value)}
                        data-testid={`roster-email-${role.role_key}`}
                      />
                    </label>
                    <label>
                      Backup (optional)
                      <input
                        type="text"
                        placeholder="Full name"
                        value={draft.backup_name || ''}
                        onChange={(e) => updateField(role.role_key, 'backup_name', e.target.value)}
                        data-testid={`roster-backup-name-${role.role_key}`}
                      />
                    </label>
                    <label>
                      <Mail size={12} /> Backup email
                      <input
                        type="email"
                        placeholder="backup@threebie.com"
                        value={draft.backup_email || ''}
                        onChange={(e) => updateField(role.role_key, 'backup_email', e.target.value)}
                        data-testid={`roster-backup-email-${role.role_key}`}
                      />
                    </label>
                  </div>

                  <div className="roster-card-footer">
                    {role.updated_at && role.updated_by && role.updated_by !== 'system_seed' && (
                      <span className="roster-updated">
                        Last updated by {role.updated_by}
                      </span>
                    )}
                    <button
                      className={`btn-primary ${!dirty ? 'btn-disabled' : ''}`}
                      disabled={!dirty || savingKey === role.role_key}
                      onClick={() => saveRole(role)}
                      data-testid={`roster-save-${role.role_key}`}
                    >
                      <Save size={14} />
                      {savingKey === role.role_key ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
                    </button>
                  </div>
                </Card>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
